import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { getDatabookInsights } from "@/lib/api/databook";
import { getQnaQuestions } from "@/lib/api/fddQa";
import { BackendError } from "@/lib/backend";
import { DatabookInspectorPanel } from "./DatabookInspectorPanel";
import type { DatabookInsightsInspectorProps } from "./DatabookInsightsInspector";

export function DatabookKeyCommentsInspector({
  insights, projectId, versionId, previewRevision, sheetName, sheetListError,
  onSelectSheet, onOpenQnaQuestion, onRetryWorkbook,
}: DatabookInsightsInspectorProps) {
  const query = useQuery({
    queryKey: ["databook-insights", projectId, versionId, previewRevision],
    queryFn: ({ signal }) => getDatabookInsights(projectId!, versionId!, signal),
    enabled: !insights && Boolean(projectId && versionId),
    staleTime: Infinity,
    retry: false,
  });
  const comments = (insights ?? query.data)?.key_comments ?? [];
  const [position, setPosition] = useState(0);
  const item = comments[position];
  const firstSource = item?.sources[0];
  const questionsQuery = useQuery({
    queryKey: ["databook-qna-register", projectId],
    queryFn: ({ signal }) => getQnaQuestions(projectId!, signal),
    enabled: Boolean(projectId && onOpenQnaQuestion && item?.question_ids.length),
    retry: false,
  });
  const linkedQuestions = questionsQuery.data?.questions.filter(question => item?.question_ids.includes(question.id)) ?? [];
  const visited = useRef<string | null>(null);
  useEffect(() => {
    const target = firstSource ? `${position}:${firstSource.sheet_name}:${firstSource.cell_address}` : null;
    if (target === visited.current) return;
    visited.current = target;
    if (firstSource && firstSource.sheet_name !== sheetName) onSelectSheet(firstSource.sheet_name);
  }, [position, firstSource, sheetName, onSelectSheet]);
  const staleVersion = query.error instanceof BackendError && query.error.status === 409;

  return (
    <DatabookInspectorPanel
      kind="key-comments" sheetName={firstSource?.sheet_name ?? "Databook"}
      position={position} total={comments.length}
      loading={!insights && !sheetListError && query.isPending}
      error={sheetListError || (!insights && query.isError)}
      onNext={() => setPosition(position >= comments.length ? 0 : position + 1)}
      onPrevious={() => setPosition(Math.max(0, position - 1))}
      previousDisabled={position === 0}
      onRetry={() => {
        if (sheetListError || staleVersion || !versionId) onRetryWorkbook();
        else void query.refetch();
      }}
    >
      {item && (
        <section className="space-y-5 py-5">
          <h3 className="break-words text-sm font-semibold [overflow-wrap:anywhere]">{item.title}</h3>
          <p className="whitespace-pre-wrap break-words text-[13px] leading-[1.65] [overflow-wrap:anywhere]">{item.takeaway}</p>
          {linkedQuestions.length > 0 && onOpenQnaQuestion && (
            <div className="space-y-2">
              <h4 className="text-xs text-muted-foreground">Related Q&A</h4>
              {linkedQuestions.map(question => (
                <button key={question.id} type="button"
                  onClick={() => onOpenQnaQuestion(question.id)}
                  className="block w-full rounded-md border px-3 py-2 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  {question.question}
                </button>
              ))}
            </div>
          )}
          <Collapsible key={position}>
            <CollapsibleTrigger className="group flex items-center gap-1.5 rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Sources · {item.sources.length}
              <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3 flex flex-wrap gap-2">
                {item.sources.map((source) => (
                  <button
                    key={`${source.sheet_name}!${source.cell_address}`}
                    type="button"
                    onClick={() => onSelectSheet(source.sheet_name)}
                    className="break-words rounded-md bg-muted px-2 py-1 text-left font-mono text-xs hover:bg-muted/70 [overflow-wrap:anywhere]"
                  >
                    {source.sheet_name}!{source.cell_address}
                  </button>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </section>
      )}
    </DatabookInspectorPanel>
  );
}
